import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import styles from './SchedulerSetting.module.scss';
import { JobCode, getJobCodes, getGeneralSettings } from '../../../core/API/guestConnect';
import RewardsList from './GuestConnect/RewardsList';
import CreateRewards from './GuestConnect/CreateRewards';
import EditRewards from './GuestConnect/EditRewards';
import EditGeneralSettings from './GuestConnect/EditGeneralSettings';
import { Edit } from '@mui/icons-material';
import { CircularProgress, Box } from '@mui/material';

type ViewMode = 'list' | 'createRewards' | 'editRewards' | 'editGeneralSettings';

const GuestConnectSetting = () => {
	const [viewMode, setViewMode] = useState<ViewMode>('list');
	const [rewards, setRewards] = useState<JobCode[]>([]);
	const [settings, setSettings] = useState<any | null>(null);
	const [editingReward, setEditingReward] = useState<JobCode | null>(null);

	const [loadingSettings, setLoadingSettings] = useState(false);
	const [loadingRewards, setLoadingRewards] = useState(false);

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoadingSettings(true);
		setLoadingRewards(true);

		try {
			const workplaceId = Number(localStorage.getItem('workplace-id'));
			const [rewardsData, settingsData] = await Promise.all([
				getJobCodes(workplaceId),
				getGeneralSettings(workplaceId),
			]);
			setRewards(rewardsData || []);
			setSettings(settingsData || null);
			setViewMode('list');
		} finally {
			setLoadingSettings(false);
			setLoadingRewards(false);
		}
	};

	const handleBackClick = () => {
		setViewMode('list');
		setEditingReward(null);
	};

	if (viewMode === 'createRewards') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateRewards backButtonClick={handleBackClick} onRewardCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editRewards' && editingReward) {
		return (
			<div className={styles.schedulerSettings}>
				<EditRewards
					reward={editingReward}
					handleBackClick={handleBackClick}
					onRewardUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'editGeneralSettings') {
		return (
			<div className={styles.schedulerSettings}>
				<EditGeneralSettings
					settings={settings || {}}
					handleBackClick={handleBackClick}
					onSettingsUpdated={loadData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>General Settings</h2>
				</div>
				<div className={styles.list}>
					<div className={styles.tableContainer}>
						{loadingSettings ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									minHeight: '200px',
								}}
							>
								<CircularProgress size={20} color="inherit" />
							</Box>
						) : (
							<table className={styles.workplaceTable}>
								<thead>
									<tr>
										<th>Disclaimer</th>
										<th>Logo</th>
										<th>Multiple Submission</th>
										<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<span>{settings?.disclaimerText ? 'Yes' : 'No'}</span>
										</td>
										<td>
											<span>{settings?.logoPath ? 'Yes' : 'No'}</span>
										</td>
										<td>
											<span>{settings?.allowMultipleSubmissionsPerDay ? 'Yes' : 'No'}</span>
										</td>
										<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
											<button
												className={styles.iconButton}
												title="Edit"
												onClick={() => {
													setViewMode('editGeneralSettings');
												}}
											>
												<Edit className={styles.editIcon} />
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>
				</div>
			</Card>

			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Guest Connect Rewards</h2>
					<Button className={styles.addButton} onClick={() => setViewMode('createRewards')}>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingRewards ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<RewardsList
							rewards={rewards}
							onRewardCreated={loadData}
							onEditClick={(reward) => {
								setEditingReward(reward);
								setViewMode('editRewards');
							}}
						/>
					)}
				</div>
			</Card>
		</div>
	);
};

export default GuestConnectSetting;
